type TimeUnit = 'DAYS' | 'HOURS' | 'MINUTES' | 'SECONDS' | 'MILLISECONDS';

interface TimestampedData<T> {
  timestamp: number;
  data: T;
}

const getTimestampDifference = (current: number, previous: number, type: TimeUnit = 'DAYS'): number => {
  let difference = 0;

  const divideBy = (divider: number): number => Math.floor((current - previous) / divider);

  switch (type) {
    case 'DAYS':
      difference = divideBy(1000 * 60 * 60 * 24);
      break;
    case 'HOURS':
      difference = divideBy(1000 * 60 * 60);
      break;
    case 'MINUTES':
      difference = divideBy(1000 * 60);
      break;
    case 'SECONDS':
      difference = divideBy(1000);
      break;
    default:
      // default to milliseconds
      difference = divideBy(1);
  }

  return difference;
};

const serializeWithTimestamp = <T>(data: T): string =>
  JSON.stringify({
    timestamp: Date.now(),
    data: data
  });

const deserializeWithTimestamp = <T>(valueInLocalStorage: string): T =>
  (JSON.parse(valueInLocalStorage) as TimestampedData<T>).data;

const getKeysFromLocalStorageThatMatchesPrefix = (prefix: string | null): string[] => {
  const keys: string[] = [];

  if (prefix === null) return keys;

  for (let i = 0; i < window.localStorage.length; i++) {
    const key = window.localStorage.key(i);
    if (key?.startsWith(prefix)) {
      keys.push(key);
    }
  }
  return keys;
};

/*
  TYPE = DAYS, HOURS, MINUTES, SECONDS
*/
const deleteFromLocalStorage = (keys: string[], frequency: number = 7, type: TimeUnit = 'DAYS'): void =>
  keys.forEach(key => {
    const valueInLocalStorage = window.localStorage.getItem(key);
    if (valueInLocalStorage) {
      try {
        const timestamp = (JSON.parse(valueInLocalStorage) as TimestampedData<unknown>).timestamp;
        const diffInMinutes = getTimestampDifference(Date.now(), timestamp, type);
        if (diffInMinutes >= frequency) {
          window.localStorage.removeItem(key);
        }
      } catch {
        // Failed to parse - remove invalid entry
        window.localStorage.removeItem(key);
      }
    }
  });

export {
  getKeysFromLocalStorageThatMatchesPrefix,
  deleteFromLocalStorage,
  serializeWithTimestamp,
  deserializeWithTimestamp
};

export type { TimeUnit, TimestampedData };
